/**
 * PublicIntakeForm Page
 * Public-facing intake form that leads fill out
 * Beautiful, branded experience with smooth animations
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import leadIntakeService from '../../services/LeadIntakeService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const PublicIntakeForm = () => {
  const { slug } = useParams();
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars

  const [form, setForm] = useState(null);
  const [photographer, setPhotographer] = useState(null);
  const [responses, setResponses] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadForm = async () => {
    try {
      // Fast: Load directly from Firestore publishedForms collection
      const publishedRef = doc(db, 'publishedForms', slug);
      const publishedSnap = await getDoc(publishedRef);

      if (publishedSnap.exists()) {
        const data = publishedSnap.data();
        const formData = {
          id: data.formId,
          name: data.name,
          description: data.description,
          questions: data.questions || [],
          styling: data.styling || {},
          userId: data.userId
        };
        setForm(formData);
        setPhotographer(data.photographer || {});

        // Initialize responses
        const initialResponses = {};
        formData.questions.forEach(q => {
          if (q.type === 'multiselect') {
            initialResponses[q.id] = [];
          } else {
            initialResponses[q.id] = '';
          }
        });
        setResponses(initialResponses);
      } else {
        // Fallback to API (for old forms not yet in publishedForms)
        const res = await leadIntakeService.getPublicForm(slug);
        setForm(res.form);
        setPhotographer(res.photographer);

        const initialResponses = {};
        res.form.questions.forEach(q => {
          if (q.type === 'multiselect') {
            initialResponses[q.id] = [];
          } else {
            initialResponses[q.id] = '';
          }
        });
        setResponses(initialResponses);
      }
    } catch (err) {
      console.error('Error loading form:', err);
      setError('Form not found');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
    // Clear error when user types
    if (errors[questionId]) {
      setErrors(prev => ({ ...prev, [questionId]: null }));
    }
  };

  const handleMultiSelect = (questionId, value) => {
    setResponses(prev => {
      const current = prev[questionId] || [];
      if (current.includes(value)) {
        return { ...prev, [questionId]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [questionId]: [...current, value] };
      }
    });
  };

  const validate = () => {
    const newErrors = {};
    form.questions.forEach(q => {
      if (q.required) {
        const value = responses[q.id];
        if (!value || (Array.isArray(value) && value.length === 0)) {
          newErrors[q.id] = 'This field is required';
        }
      }
      // Email validation
      if (q.type === 'email' && responses[q.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(responses[q.id])) {
          newErrors[q.id] = 'Please enter a valid email';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      // Scroll to first error
      const firstError = document.querySelector('[data-error="true"]');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    try {
      await leadIntakeService.submitForm(form.id, responses);
      setSubmitted(true);
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to submit. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const styling = form?.styling || {};
  const buttonRadius = styling.buttonStyle === 'pill' ? 'rounded-full' :
                       styling.buttonStyle === 'square' ? 'rounded-none' : 'rounded-xl';
  const formMaxWidth = styling.formWidth === 'narrow' ? 'max-w-md' :
                       styling.formWidth === 'wide' ? 'max-w-2xl' : 'max-w-lg';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: styling.backgroundColor || '#FFFFFF' }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="w-8 h-8 border-2 border-t-transparent rounded-full"
          style={{ borderColor: styling.primaryColor || '#FFB27D' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-gray-500">This form may have been removed or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: styling.backgroundColor || '#FFFFFF' }}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center max-w-md"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: `${styling.primaryColor || '#FFB27D'}20` }}
          >
            <CheckCircleIcon className="w-10 h-10" style={{ color: styling.primaryColor || '#FFB27D' }} />
          </motion.div>
          <h1
            className="text-2xl font-bold mb-2"
            style={{ color: styling.textColor || '#111111' }}
          >
            Thank you!
          </h1>
          <p style={{ color: `${styling.textColor || '#111111'}99` }}>
            Your response has been submitted. {photographer?.name || 'We'} will be in touch soon!
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-8 px-4"
      style={{ backgroundColor: styling.backgroundColor || '#FFFFFF' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`mx-auto ${formMaxWidth}`}
      >
        {/* Header */}
        {styling.headerImage && (
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            src={styling.headerImage}
            alt=""
            className="w-full h-48 object-cover rounded-2xl mb-6"
          />
        )}

        <div className="mb-8">
          {styling.logo && (
            <img src={styling.logo} alt={photographer?.businessName || ''} className="h-12 mb-4" />
          )}
          <h1
            className="text-3xl font-bold mb-2"
            style={{ color: styling.textColor || '#111111' }}
          >
            {form.name}
          </h1>
          {form.description && (
            <p style={{ color: `${styling.textColor || '#111111'}80` }}>
              {form.description}
            </p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {form.questions.map((question, index) => (
            <motion.div
              key={question.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-error={!!errors[question.id]}
            >
              <label
                className="block font-medium mb-2"
                style={{ color: styling.textColor || '#111111' }}
              >
                {question.label}
                {question.required && (
                  <span style={{ color: styling.primaryColor || '#FFB27D' }}> *</span>
                )}
              </label>

              {/* Text Input */}
              {question.type === 'text' && (
                <input
                  type="text"
                  value={responses[question.id] || ''}
                  onChange={(e) => handleChange(question.id, e.target.value)}
                  placeholder={question.placeholder}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none ${
                    errors[question.id] ? 'border-red-400' : 'border-gray-200 focus:border-opacity-50'
                  }`}
                  style={{
                    backgroundColor: styling.backgroundColor || '#FFFFFF',
                    color: styling.textColor || '#111111',
                    borderColor: errors[question.id] ? undefined : (styling.primaryColor ? `${styling.primaryColor}40` : undefined)
                  }}
                />
              )}

              {/* Email Input */}
              {question.type === 'email' && (
                <input
                  type="email"
                  value={responses[question.id] || ''}
                  onChange={(e) => handleChange(question.id, e.target.value)}
                  placeholder={question.placeholder || 'your@email.com'}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none ${
                    errors[question.id] ? 'border-red-400' : 'border-gray-200'
                  }`}
                  style={{
                    backgroundColor: styling.backgroundColor || '#FFFFFF',
                    color: styling.textColor || '#111111'
                  }}
                />
              )}

              {/* Phone Input */}
              {question.type === 'phone' && (
                <input
                  type="tel"
                  value={responses[question.id] || ''}
                  onChange={(e) => handleChange(question.id, e.target.value)}
                  placeholder={question.placeholder || '(555) 123-4567'}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none ${
                    errors[question.id] ? 'border-red-400' : 'border-gray-200'
                  }`}
                  style={{
                    backgroundColor: styling.backgroundColor || '#FFFFFF',
                    color: styling.textColor || '#111111'
                  }}
                />
              )}

              {/* Textarea */}
              {question.type === 'textarea' && (
                <textarea
                  value={responses[question.id] || ''}
                  onChange={(e) => handleChange(question.id, e.target.value)}
                  placeholder={question.placeholder}
                  rows={4}
                  className={`w-full px-4 py-3 rounded-xl border-2 resize-none transition-all focus:outline-none ${
                    errors[question.id] ? 'border-red-400' : 'border-gray-200'
                  }`}
                  style={{
                    backgroundColor: styling.backgroundColor || '#FFFFFF',
                    color: styling.textColor || '#111111'
                  }}
                />
              )}

              {/* Date Input */}
              {question.type === 'date' && (
                <input
                  type="date"
                  value={responses[question.id] || ''}
                  onChange={(e) => handleChange(question.id, e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all focus:outline-none ${
                    errors[question.id] ? 'border-red-400' : 'border-gray-200'
                  }`}
                  style={{
                    backgroundColor: styling.backgroundColor || '#FFFFFF',
                    color: styling.textColor || '#111111'
                  }}
                />
              )}

              {/* Select / Budget */}
              {(question.type === 'select' || question.type === 'budget') && (
                <div className="space-y-2">
                  {question.options?.map((option) => (
                    <motion.button
                      key={option.value}
                      type="button"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => handleChange(question.id, option.value)}
                      className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                        responses[question.id] === option.value ? 'border-opacity-100' : 'border-gray-200 border-opacity-50'
                      }`}
                      style={{
                        backgroundColor: responses[question.id] === option.value
                          ? `${styling.primaryColor || '#FFB27D'}15`
                          : styling.backgroundColor || '#FFFFFF',
                        borderColor: responses[question.id] === option.value
                          ? styling.primaryColor || '#FFB27D'
                          : undefined,
                        color: styling.textColor || '#111111'
                      }}
                    >
                      {option.label}
                    </motion.button>
                  ))}
                </div>
              )}

              {/* Multiselect */}
              {question.type === 'multiselect' && (
                <div className="space-y-2">
                  {question.options?.map((option) => {
                    const isSelected = (responses[question.id] || []).includes(option.value);
                    return (
                      <motion.button
                        key={option.value}
                        type="button"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => handleMultiSelect(question.id, option.value)}
                        className="w-full px-4 py-3 rounded-xl border-2 text-left transition-all flex items-center gap-3"
                        style={{
                          backgroundColor: isSelected
                            ? `${styling.primaryColor || '#FFB27D'}15`
                            : styling.backgroundColor || '#FFFFFF',
                          borderColor: isSelected
                            ? styling.primaryColor || '#FFB27D'
                            : '#E5E5E5',
                          color: styling.textColor || '#111111'
                        }}
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            isSelected ? '' : 'border-gray-300'
                          }`}
                          style={{
                            backgroundColor: isSelected ? styling.primaryColor || '#FFB27D' : 'transparent',
                            borderColor: isSelected ? styling.primaryColor || '#FFB27D' : undefined
                          }}
                        >
                          {isSelected && <CheckCircleIcon className="w-3 h-3 text-white" />}
                        </div>
                        {option.label}
                      </motion.button>
                    );
                  })}
                </div>
              )}

              {/* Rating */}
              {question.type === 'rating' && (
                <div className="flex gap-2">
                  {Array.from({ length: question.scale || 5 }, (_, i) => i + 1).map((rating) => (
                    <motion.button
                      key={rating}
                      type="button"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleChange(question.id, rating)}
                      className="p-2"
                    >
                      {responses[question.id] >= rating ? (
                        <StarIconSolid
                          className="w-8 h-8"
                          style={{ color: styling.primaryColor || '#FFB27D' }}
                        />
                      ) : (
                        <StarIcon
                          className="w-8 h-8"
                          style={{ color: `${styling.textColor || '#111111'}30` }}
                        />
                      )}
                    </motion.button>
                  ))}
                </div>
              )}

              {/* Error Message */}
              <AnimatePresence>
                {errors[question.id] && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-red-500 text-sm mt-1"
                  >
                    {errors[question.id]}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          ))}

          {/* Submit Error */}
          {errors.submit && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm"
            >
              {errors.submit}
            </motion.div>
          )}

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={submitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full py-4 font-semibold text-lg transition-all disabled:opacity-60 ${buttonRadius}`}
            style={{
              backgroundColor: styling.primaryColor || '#FFB27D',
              color: '#111111'
            }}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                  className="w-5 h-5 border-2 border-current border-t-transparent rounded-full inline-block"
                />
                Submitting...
              </span>
            ) : (
              'Submit'
            )}
          </motion.button>
        </form>

        {/* Powered by Klorah */}
        <div className="mt-8 text-center">
          <a
            href="https://klorah.app"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm opacity-50 hover:opacity-70 transition-opacity"
            style={{ color: styling.textColor || '#111111' }}
          >
            Powered by Klorah
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default PublicIntakeForm;
